import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { useCopilotChat } from "../hooks/useCopilotChat";
import "../styles/CopilotPage.css";

function formatUpdatedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function CopilotPage({ user, showToast }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem("sb-collapsed") === "true"
  );

  const {
    conversationId,
    messages,
    sending,
    send,
    startNewConversation,
    conversations,
    loadingConversations,
    loadConversation,
    models,
    selectedProvider,
    selectProvider,
    activeConversation,
  } = useCopilotChat({ showToast });
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  function handleSubmit(e) {
    e.preventDefault();
    const text = input;
    setInput("");
    send(text);
  }

  return (
    <div
      className="cp-shell"
      style={{
        gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr`,
      }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => {
          setSidebarCollapsed((c) => {
            const next = !c;
            localStorage.setItem("sb-collapsed", String(next));
            return next;
          });
        }}
      />

      <div className="cp-main">
        <div className="page-head">
          <h1>AI Copilot</h1>
          <div className="page-head-actions">
            {conversationId ? (
              // An existing conversation's model is fixed at creation and
              // shown read-only — switching mid-conversation isn't
              // supported (prior tool-call history is in that provider's
              // own wire format).
              activeConversation && (
                <span className="cp-model-badge" title="Model used for this conversation">
                  {models.find((m) => m.provider === activeConversation.provider)?.label
                    || activeConversation.provider}
                </span>
              )
            ) : (
              models.length > 1 && (
                <select
                  className="cp-model-select"
                  value={selectedProvider || ""}
                  onChange={(e) => selectProvider(e.target.value)}
                  title="Model for the next new conversation"
                >
                  {models.map((m) => (
                    <option key={m.provider} value={m.provider}>{m.label}</option>
                  ))}
                </select>
              )
            )}
            <button className="cp-new-btn v2-btn-primary" onClick={startNewConversation}>
              + New conversation
            </button>
          </div>
        </div>

        <div className="cp-body">
          <div className="cp-history">
            <div className="cp-history-title">Your conversations</div>
            {loadingConversations && <div className="cp-history-empty">Loading…</div>}
            {!loadingConversations && conversations.length === 0 && (
              <div className="cp-history-empty">No conversations yet.</div>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                className={`cp-history-item${c.id === conversationId ? " cp-history-item--active" : ""}`}
                onClick={() => loadConversation(c.id)}
                title={c.title}
              >
                <div className="cp-history-item-title">{c.title || "Untitled"}</div>
                <div className="cp-history-item-meta">
                  <span className="cp-history-item-provider">{c.provider}</span>
                  <span className="cp-history-item-time">{formatUpdatedAt(c.updated_at)}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="cp-chat-wrap">
            <div className="cp-messages" ref={scrollRef}>
              {messages.length === 0 && (
                <div className="cp-empty">
                  Ask about tasks, defects, or discussion threads — e.g. "what
                  tasks are overdue for Acme" or "summarize the discussion on
                  defect DEF-1234".
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`cp-bubble cp-bubble--${m.role}`}>
                  <div className="cp-bubble-role">{m.role === "user" ? "You" : "Copilot"}</div>
                  <div className="cp-bubble-content">
                    {/* react-markdown parses to React elements only — it never
                        injects raw HTML (no rehype-raw plugin), so this stays
                        safe against untrusted content the same way the plain-
                        text render did. */}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  </div>
                </div>
              ))}
              {sending && (
                <div className="cp-bubble cp-bubble--assistant cp-bubble--pending">
                  <div className="cp-bubble-role">Copilot</div>
                  <div className="cp-bubble-content">Thinking…</div>
                </div>
              )}
            </div>

            <form className="cp-input-row" onSubmit={handleSubmit}>
              <input
                type="text"
                className="cp-input"
                placeholder="Ask the AI Copilot…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending}
                maxLength={4000}
              />
              <button type="submit" className="cp-send-btn v2-btn-primary" disabled={sending || !input.trim()}>
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
